import { useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router";
import type { ComponentType, SVGProps, MouseEvent } from "react";
import type { To } from "react-router";
import StarsLinear from "~icons/solar/stars-linear";
import StarsBold from "~icons/solar/stars-bold";
import CalendarLinear from "~icons/solar/calendar-linear";
import CalendarBold from "~icons/solar/calendar-bold";
import FolderLinear from "~icons/solar/folder-linear";
import FolderBold from "~icons/solar/folder-bold";
import BuildingsLinear from "~icons/solar/buildings-2-linear";
import BuildingsBold from "~icons/solar/buildings-2-bold";
import AtomLinear from "~icons/solar/atom-linear";
import AtomBold from "~icons/solar/atom-bold";
import UsersGroupTwoRoundedLinear from "~icons/solar/users-group-two-rounded-linear";
import UsersGroupTwoRoundedBold from "~icons/solar/users-group-two-rounded-bold";
import ChatRoundLikeLinear from "~icons/solar/chat-round-like-linear";
import ChatRoundLikeBold from "~icons/solar/chat-round-like-bold";
import UsersGroupRoundedLinear from "~icons/solar/users-group-rounded-linear";
import UsersGroupRoundedBold from "~icons/solar/users-group-rounded-bold";
import CompassLinear from "~icons/solar/compass-linear";
import CompassBold from "~icons/solar/compass-bold";
import GraphLinear from "~icons/solar/graph-linear";
import GraphBold from "~icons/solar/graph-bold";
import ChartSquareLinear from "~icons/solar/chart-square-linear";
import ChartSquareBold from "~icons/solar/chart-square-bold";
import { useChatStore } from "../../stores/chat-store";
import { useAuthStore } from "../../stores/auth-store";
import { usePermissions } from "../../hooks/usePermissions";
import { env } from "../../lib/env";
import { SEARCH_TRACE_ALLOWED_EMAIL } from "../../lib/search-trace-allowlist";
import { Permissions } from "../../types/permissions";
import type { Permission } from "../../types/permissions";

const FEEDBACK_ENABLED = env.FEATURE_FEEDBACK_ENABLED;
const SEARCH_TRACE_ENABLED = env.SEARCH_TRACE_ENABLED;
const ANALYTICS_ENABLED = env.FEATURE_ANALYTICS_ENABLED;

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

/** Sidebar sections. `primary` renders without a heading. */
export type NavGroupKey = "primary" | "tools" | "admin";

export interface NavItemDef {
  to: To;
  icon: IconType;
  /** Filled variant shown while the route is active. */
  activeIcon?: IconType;
  label: string;
  end?: boolean;
  /** If set, item is rendered only when the user has this permission. */
  requiredPermission?: Permission;
  /** Defaults to "primary". */
  group?: NavGroupKey;
  onClick?: (e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
}

export interface NavGroup {
  key: NavGroupKey;
  /** null for the primary group. */
  label: string | null;
  items: NavItemDef[];
}

const GROUP_ORDER: { key: NavGroupKey; label: string | null }[] = [
  { key: "primary", label: null },
  { key: "tools", label: "Tools" },
  { key: "admin", label: "Admin" },
];

export function useSidebarNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const userEmail = useAuthStore((s) => s.user?.email);
  const searchTraceVisible =
    SEARCH_TRACE_ENABLED && userEmail === SEARCH_TRACE_ALLOWED_EMAIL;

  // "Smart Scout" sidebar entry: resume the active conversation if one
  // exists in the store; otherwise land on a fresh /chat. This pairs with
  // the persistent floating panel so messages survive navigation. Use the
  // "New Chat" button in ChatHistoryPanel (or the floating panel header)
  // to explicitly start a new thread.
  const handleNewChat = useCallback(
    (e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      e.preventDefault();
      const { messages, threadId } = useChatStore.getState();
      const target =
        messages.length > 0 ? `/chat?t=${threadId}` : "/chat";
      navigate(target, { replace: location.pathname === "/chat" });
    },
    [navigate, location.pathname]
  );

  const navItems = useMemo<NavItemDef[]>(() => {
    const allItems: NavItemDef[] = [
      // --- primary (v3 order) ---
      {
        to: "/chat",
        icon: StarsLinear,
        activeIcon: StarsBold,
        label: "Smart Scout",
        onClick: handleNewChat,
        requiredPermission: Permissions.AI_CHAT,
      },
      {
        to: "/meetings",
        icon: CalendarLinear,
        activeIcon: CalendarBold,
        label: "Meetings",
      },
      {
        to: "/projects",
        icon: FolderLinear,
        activeIcon: FolderBold,
        label: "Projects",
        requiredPermission: Permissions.PROJECT_VIEW,
      },
      {
        to: "/clients",
        icon: BuildingsLinear,
        activeIcon: BuildingsBold,
        label: "Clients",
        requiredPermission: Permissions.CLIENT_VIEW,
      },
      {
        to: "/companies",
        icon: AtomLinear,
        activeIcon: AtomBold,
        label: "Companies",
        requiredPermission: Permissions.COMPANY_VIEW,
      },
      {
        to: "/talent-pools",
        icon: UsersGroupTwoRoundedLinear,
        activeIcon: UsersGroupTwoRoundedBold,
        label: "Talent Pools",
        requiredPermission: Permissions.TALENT_POOL_VIEW,
      },
      {
        to: "/talent",
        icon: CompassLinear,
        activeIcon: CompassBold,
        label: "Discover Talent",
        requiredPermission: Permissions.CANDIDATE_SEARCH,
      },
      // --- tools ---
      ...(FEEDBACK_ENABLED
        ? [
            {
              to: "/feedback" as const,
              icon: ChatRoundLikeLinear,
              activeIcon: ChatRoundLikeBold,
              label: "Feedback",
              group: "tools" as const,
            },
          ]
        : []),
      ...(ANALYTICS_ENABLED
        ? [
            {
              to: "/analytics" as const,
              icon: ChartSquareLinear,
              activeIcon: ChartSquareBold,
              label: "Analytics",
              requiredPermission: Permissions.ANALYTICS_VIEW_OWN,
              group: "tools" as const,
            },
          ]
        : []),
      // --- admin ---
      {
        to: "/admin/users",
        icon: UsersGroupRoundedLinear,
        activeIcon: UsersGroupRoundedBold,
        label: "Users",
        requiredPermission: Permissions.USER_VIEW,
        group: "admin",
      },
      ...(searchTraceVisible
        ? [
            {
              to: "/admin/search-traces",
              icon: GraphLinear,
              activeIcon: GraphBold,
              label: "Search Traces",
              requiredPermission: Permissions.SEARCH_TRACE_VIEW,
              group: "admin" as const,
            },
          ]
        : []),
    ];

    return allItems.filter(
      (item) => !item.requiredPermission || hasPermission(item.requiredPermission)
    );
  }, [handleNewChat, hasPermission, searchTraceVisible]);

  const navGroups = useMemo<NavGroup[]>(
    () =>
      GROUP_ORDER.map(({ key, label }) => ({
        key,
        label,
        items: navItems.filter((i) => (i.group ?? "primary") === key),
      })).filter((g) => g.items.length > 0),
    [navItems]
  );

  return { navItems, navGroups, handleNewChat };
}
